import { RBAClient } from 'rba-client'

const client = RBAClient.getInstance()

const state = {
  listing: [],
  totalCount: 0,
  selectedAlert: null,
  analysis: null,
  loading: false,
  error: null,

  // Default query parameters matching GET /api/v1/alerts
  filters: {
    page: 1,
    limit: 20,
    status: null, // 'OPEN' | 'INVESTIGATING' | 'ESCALATED' | 'CLOSED_RESOLVED' | 'CLOSED_FALSE_POSITIVE'
    partyId: '',
    accountId: '',
    ruleCode: '',
    assignedTo: '',
    sortBy: 'create_date',
    sortOrder: 'desc'
  },

  // Enums extracted directly from Swagger OpenAPI DTOs and Alert schemas
  enums: {
    statuses: [
      'OPEN',
      'INVESTIGATING',
      'ESCALATED',
      'CLOSED_RESOLVED',
      'CLOSED_FALSE_POSITIVE'
    ]
  }
}

const getters = {
  alerts: (state) => state.listing,
  totalCount: (state) => state.totalCount,
  selectedAlert: (state) => state.selectedAlert,
  alertsAnalysis: (state) => state.analysis,
  isLoading: (state) => state.loading,
  error: (state) => state.error,

  // Sanitizes filters to remove empty strings, nulls, or undefined values before sending API requests
  activeFilters: (state) => {
    return Object.entries(state.filters).reduce((acc, [key, val]) => {
      if (val !== null && val !== '' && val !== undefined) {
        acc[key] = val
      }
      return acc
    }, {})
  },

  // UI Dropdown option helpers derived from API enums
  statusOptions: (state) =>
    state.enums.statuses.map((st) => ({
      label: st.replace(/_/g, ' '),
      value: st
    }))
}

const mutations = {
  SET_LOADING (state, isLoading) {
    state.loading = isLoading
  },
  SET_ERROR (state, error) {
    state.error = error
  },
  SET_ALERTS (state, { items, total }) {
    state.listing = items
    state.totalCount = total
  },
  SET_SELECTED_ALERT (state, alert) {
    state.selectedAlert = alert
  },
  SET_ANALYSIS (state, analysisData) {
    state.analysis = analysisData
  },
  UPDATE_ALERT_IN_LIST (state, updatedAlert) {
    const index = state.listing.findIndex((item) => item.id === updatedAlert.id)
    if (index !== -1) {
      state.listing.splice(index, 1, { ...state.listing[index], ...updatedAlert })
    }
    if (state.selectedAlert && state.selectedAlert.id === updatedAlert.id) {
      state.selectedAlert = { ...state.selectedAlert, ...updatedAlert }
    }
  },
  UPDATE_FILTERS (state, payload) {
    state.filters = { ...state.filters, ...payload }
  },
  RESET_FILTERS (state) {
    state.filters = {
      page: 1,
      limit: 20,
      status: null,
      partyId: '',
      accountId: '',
      ruleCode: '',
      assignedTo: '',
      sortBy: 'create_date',
      sortOrder: 'desc'
    }
  }
}

const actions = {
  // GET /api/v3/transaction_monitoring/alerts
  async fetchAlerts ({ commit, getters }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const params = getters.activeFilters

      const response = client.tm_alert
        ? await client.tm_alert.findWhere({ where: params })
        : await client.http.get('/api/v1/alerts', { params })

      const data = response.data || response
      const items = Array.isArray(data) ? data : (data.items || data.data || [])
      const total = Array.isArray(data) ? data.length : (data.total || data.count || items.length)

      commit('SET_ALERTS', { items, total })
      return response
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch alerts'
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // GET /api/v3/transaction_monitoring/alerts?tranId=XYZ
  async fetchAlertsByTranId ({ commit }, tranId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const params = { ...getters.activeFilters, tranId: tranId }

      const response = client.tm_alert ? await client.tm_alert.findWhere({ where: params }) : await client.http.get('/api/v3/transaction_monitoring/alerts', { params })

      const data = response.data || response
      commit('SET_SELECTED_ALERT', data)
      return data
    } catch (err) {
      const message = err.response?.data?.message || err.message || `Failed to fetch alert ${tranId}`
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // GET /api/v3/transaction_monitoring/alerts/{id}
  async fetchAlertById ({ commit }, id) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const params = getters.activeFilters

      // Log available keys on the SDK client to verify exact method/property names
      console.log('RBAClient properties:', Object.keys(client))

      const response = client.tm_alert ? await client.tm_alert.findOne(id) : await client.http.get(`/api/v3/transaction_monitoring/alerts/${id}`)

      const data = response.data || response
      commit('SET_SELECTED_ALERT', data)
      return data
    } catch (err) {
      const message = err.response?.data?.message || err.message || `Failed to fetch alert ${id}`
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // PATCH /api/v3/transaction_monitoring/alerts/{id} (UpdateAlertDto)
  // async updateAlertStatus ({ commit }, { id, status, assignedTo }) {
  //   commit('SET_LOADING', true)
  //   commit('SET_ERROR', null)
  //   try {
  //     const payload = { status, assignedTo }

  //     const response = client.tm_alert
  //       ? await client.tm_alert.update(id, payload)
  //       : await client.http.patch(`/api/v1/alerts/${id}`, payload)

  //     const updatedData = response.data || response
  //     commit('UPDATE_ALERT_IN_LIST', updatedData)
  //     return updatedData
  //   } catch (err) {
  //     const message = err.response?.data?.message || err.message || `Failed to update alert ${id}`
  //     commit('SET_ERROR', message)
  //     throw err
  //   } finally {
  //     commit('SET_LOADING', false)
  //   }
  // },

  async updateAlertStatus ({ commit }, { id, status, remark, assignedTo }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
    // Construct payload including remark for backend audit persistence
      const payload = {}
      if (status !== undefined) payload.status = status
      if (remark !== undefined) payload.remark = remark
      if (assignedTo !== undefined) payload.assignedTo = assignedTo

      const response = client.tm_alert
        ? await client.tm_alert.update(id, payload)
        : await client.http.patch(`/api/v1/alerts/${id}`, payload)

      const updatedData = response.data || response
      commit('UPDATE_ALERT_IN_LIST', updatedData)
      return updatedData
    } catch (err) {
      const message = err.response?.data?.message || err.message || `Failed to update alert ${id}`
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // GET /api/v3/transaction_monitoring/alerts/analysis
  async fetchAlertsAnalysis ({ commit }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = client.tm_alert && client.tm_alert.analysis
        ? await client.tm_alert.analysis()
        : await client.http.get('/api/v1/alerts/analysis')

      const data = response.data || response
      commit('SET_ANALYSIS', data)
      return data
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch alert analysis'
      commit('SET_ERROR', message)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Filter updates triggered by table controls
  setFilters ({ commit, dispatch }, filterPayload) {
    commit('UPDATE_FILTERS', filterPayload)
    return dispatch('fetchAlerts')
  },

  resetFilters ({ commit, dispatch }) {
    commit('RESET_FILTERS')
    return dispatch('fetchAlerts')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
